import { APP_BUILD_NUMBER, APP_VERSION } from '@/config/version';
import router from '@/router';
import axios from '@/utils/axios';
import { App } from '@capacitor/app';
import { Capacitor, CapacitorHttp, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { computed, ref } from 'vue';

// Define a unified response type to avoid axios type conflicts
interface UnifiedResponse {
  data: unknown;
  status: number;
  headers: Record<string, string>;
}

export function usePlatform() {
  // Platform detection
  const isNative = computed(() => Capacitor.isNativePlatform());
  const isWeb = computed(() => !Capacitor.isNativePlatform());
  const platform = computed(() => Capacitor.getPlatform());
  const isAndroid = computed(() => platform.value === 'android');
  const isIOS = computed(() => platform.value === 'ios');

  // Network status (for mobile)
  const isOnline = ref(true);

  // Initialize platform-specific features
  async function initPlatform() {
    if (isNative.value) {
      // Initialize network monitoring for mobile
      const status = await Network.getStatus();
      isOnline.value = status.connected;

      Network.addListener('networkStatusChange', (status) => {
        isOnline.value = status.connected;
      });

      // Capacitor 8 handles modern edge-to-edge layouts and injects Android
      // safe-area CSS variables when the WebView cannot provide env() values.
      await SystemBars.setStyle({ style: SystemBarsStyle.Light });

      // Handle Android back button
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          // If there's history, go back
          router.back();
        } else {
          // If we're at the root, minimize the app instead of exiting
          App.exitApp();
        }
      });
    }
  }

  // Storage management (unified API for both platforms)
  async function setStorage(key: string, value: string) {
    if (isNative.value) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  }

  async function getStorage(key: string): Promise<string | null> {
    if (isNative.value) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  }

  async function removeStorage(key: string) {
    if (isNative.value) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }

  async function clearStorage() {
    if (isNative.value) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  }

  // Token management for mobile
  async function setAuthToken(token: string) {
    if (isNative.value) {
      await Preferences.set({ key: 'auth_token', value: token });
    } else {
      localStorage.setItem('auth_token', token);
    }
  }

  async function getAuthToken(): Promise<string | null> {
    if (isNative.value) {
      const { value } = await Preferences.get({ key: 'auth_token' });
      return value;
    } else {
      return localStorage.getItem('auth_token');
    }
  }

  async function removeAuthToken() {
    if (isNative.value) {
      await Preferences.remove({ key: 'auth_token' });
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  // Helper function to deep clone and serialize data
  function serializeForNative(data: unknown): unknown {
    if (data === null || data === undefined) {
      console.log('[usePlatform] Data is null or undefined, returning as is.');
      return data;
    }

    // Handle FormData separately
    if (data instanceof FormData) {
      console.log('[usePlatform] Data is FormData, returning as is.');
      console.log('FormData contents:');
      for (const [key, value] of data.entries()) {
        console.log(`  ${key}:`, value);
      }
      return data;
    }

    try {
      // Use JSON to ensure data is serializable
      const serialized = JSON.stringify(data);
      console.log('[usePlatform] Data serialized successfully:', {
        originalType: typeof data,
        serializedType: typeof serialized,
      });
      return JSON.parse(serialized);
    } catch (error) {
      console.error('[usePlatform] Failed to serialize data:', error);
      throw new Error('Data is not serializable for native request');
    }
  }

  async function makeRequest(options: {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: unknown;
    headers?: Record<string, string>;
    timeout?: number;
  }): Promise<UnifiedResponse> {
    console.log('[makeRequest] Starting request:', {
      url: options.url,
      method: options.method,
      hasData: !!options.data,
      dataType: options.data?.constructor?.name,
    });

    // Get version information for headers
    let appVersion = APP_VERSION;
    let buildNumber = APP_BUILD_NUMBER;

    if (isNative.value) {
      try {
        const appInfo = await App.getInfo();
        appVersion = appInfo.version;
        buildNumber = appInfo.build;
      } catch (error) {
        console.warn('[makeRequest] Could not get app info:', error);
      }
    }

    if (isNative.value) {
      const authToken = await getAuthToken();

      const headers: Record<string, string> = {
        Accept: 'application/json',
        'App-Version': appVersion,
        'App-Platform': platform.value,
        'App-Build': buildNumber,
        ...options.headers,
      };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      let requestData: unknown = options.data;
      console.log('[makeRequest] Preparing data for native request:', {
        originalType: options.data?.constructor?.name,
      });
      console.log('Original data: (object) ');
      // loop on option data
      if (options.data && typeof options.data === 'object' && !(options.data instanceof FormData)) {
        for (const [key, value] of Object.entries(options.data as Record<string, unknown>)) {
          console.log(`  ${key}:`, value);
        }
      } else {
        console.log('  Data is not an object or is FormData.');
        console.log('  Data:', options.data);
        // loop on form data if it's form data
        if (options.data instanceof FormData) {
          console.log('FormData contents:');
          for (const [key, value] of options.data.entries()) {
            console.log(`  ${key}:`, value);
          }
        }
      }

      // Handle different data types for native platforms
      if (options.data instanceof FormData) {
        // Convert FormData to plain object for CapacitorHttp
        const formObject: Record<string, unknown> = {};

        console.log('[makeRequest] Converting FormData to object');

        for (const [key, value] of options.data.entries()) {
          console.log(`[makeRequest] Processing FormData entry: ${key}`, typeof value);

          if (value instanceof File) {
            // Convert File to base64
            const base64 = await convertFileToBase64(value);
            // Store as plain base64 string for backend compatibility
            formObject[key] = base64;
            console.log(`[makeRequest] Converted File "${key}" to base64 (length: ${base64.length})`);
          } else {
            // Handle regular form fields
            let processedValue: unknown = value;

            // Try to parse JSON strings back to objects/arrays
            if (typeof value === 'string') {
              try {
                processedValue = JSON.parse(value);
                console.log(`[makeRequest] Parsed "${key}" as JSON:`, typeof processedValue);
              } catch {
                // If it's not JSON, use as-is
                processedValue = value;
                console.log(`[makeRequest] Using "${key}" as string`);
              }
            }

            formObject[key] = processedValue;
          }
        }

        requestData = formObject;
        // Set Content-Type to application/json since we converted FormData to a plain object
        headers['Content-Type'] = 'application/json';

        console.log('[makeRequest] Converted FormData to object with keys:', Object.keys(formObject));
      } else if (options.data) {
        // Serialize regular data
        try {
          requestData = serializeForNative(options.data);
          console.log('[makeRequest] Serialized data for native:', {
            original: typeof options.data,
            serialized: typeof requestData,
          });
        } catch (error) {
          console.error('[makeRequest] Data serialization failed:', error);
          throw error;
        }
      }

      console.log('[makeRequest] Making native request with data type:', typeof requestData);

      const response = await CapacitorHttp.request({
        url: options.url,
        method: options.method,
        headers: headers,
        data: requestData,
        connectTimeout: options.timeout || 30000,
        readTimeout: options.timeout || 30000,
        disableRedirects: false,
        responseType: 'json',
      });

      console.log('[makeRequest] Native request completed:');
      console.log('  Status:', response.status);
      console.log('hasData:', response.data !== undefined && response.data !== null);
      if (response.data && typeof response.data === 'object') {
        console.log('  Data type:', response.data.constructor.name);
        console.log('  Data contents:');
        for (const [key, value] of Object.entries(response.data as Record<string, unknown>)) {
          console.log(`    ${key}:`, value);
        }
      } else {
        console.log('  Data:', response.data);
      }

      return {
        data: response.data,
        status: response.status,
        headers: response.headers as Record<string, string>,
      };
    } else {
      // Web: Use axios
      const response = await axios({
        url: options.url,
        method: options.method,
        data: options.data,
        headers: {
          'App-Version': appVersion,
          'App-Platform': 'web',
          'App-Build': buildNumber,
          ...options.headers,
        },
        timeout: options.timeout || 60000,
        withCredentials: true,
      });

      const responseHeaders: Record<string, string> = {};
      if (response.headers) {
        Object.entries(response.headers).forEach(([key, value]) => {
          responseHeaders[key] = String(value);
        });
      }

      return {
        data: response.data,
        status: response.status,
        headers: responseHeaders,
      };
    }
  }

  // Helper function to convert File to base64
  function convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Platform-specific navigation
  // function handleDeepLink(url: string) {
  //   if (isNative.value) {
  // Handle deep linking for mobile apps
  // Add your deep link logic here
  //   }
  // }

  // Platform-specific UI adjustments
  const platformClasses = computed(() => ({
    'is-native': isNative.value,
    'is-web': isWeb.value,
    'is-android': isAndroid.value,
    'is-ios': isIOS.value,
    'is-offline': !isOnline.value,
  }));

  const isMobilePhone = computed(() => {
    if (isNative.value) {
      // On native, use screen width to distinguish phone vs tablet
      return window.innerWidth < 768;
    }
    // On web, combine screen size with user agent check
    const mobileUA = /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return mobileUA && window.innerWidth < 768;
  });

  return {
    // Platform detection
    isNative,
    isWeb,
    platform,
    isAndroid,
    isIOS,

    // Network
    isOnline,

    // Methods
    initPlatform,
    setStorage,
    getStorage,
    removeStorage,
    clearStorage,
    setAuthToken,
    getAuthToken,
    removeAuthToken,
    makeRequest,
    // handleDeepLink,

    // UI helpers
    platformClasses,
    isMobilePhone,
  };
}
